import React from 'react';
import {useAutoList} from '@netx/core-next/hooks/autoList/useAutoList';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {faUsers} from '@netx/core-icons';
import {
	AutoListField,
	AutoListFieldOption,
	type AutoListCommonExtendProps,
} from '@netx/core-components/AutoListField';
import {UserPermission} from '../../constants';
import {getUserGroupAutoList} from '../../api';
import type {IUserGroupAutoListItem} from '../../schema';
import {UserGroupAutoListLabel} from './locales';

export type UserGroupAutoListRef = React.ForwardedRef<HTMLDivElement>;

/**
 * Default get option label fn
 * @param option Option
 * @returns
 */
const defaultGetOptionLabel = (option: IUserGroupAutoListItem) =>
	option?.name ?? '';

/**
 * Default get option selected fn
 * @param option Option
 * @param value Value
 */
const defaultGetOptionSelected = (
	option?: IUserGroupAutoListItem,
	value?: IUserGroupAutoListItem,
) => {
	if (value == null) return false;
	return Array.isArray(value)
		? value.some((v) => v.type === option?.type && v.id === option?.id)
		: option?.id === value.id && option?.type === value.type;
};

/** should query string be considered valid */
const isQueryValid = (query?: string) => !!query && query !== '""';

/** should option be disabled */
const defaultGetOptionDisabled = (option: IUserGroupAutoListItem) =>
	option.id === 0;

// TODO: some major type reckoning needs to happen here
// we may not have a value/option/input but we should know what it is
const testValueContains = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	thing: string | IUserGroupAutoListItem | null,
	value?: string | UserGroupAutoListProps<Multiple, DisableClearable>['value'],
) => {
	if (thing == null || typeof thing === 'string') return false;
	if (value == null || typeof value === 'string') return false;
	return Array.isArray(value)
		? value.some((v) => v.type === thing.type && v.id === thing.id)
		: thing.type === value?.type && thing.id === value?.id;
};

export interface UserGroupAutoListProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> extends AutoListCommonExtendProps<
	IUserGroupAutoListItem,
	Multiple,
	DisableClearable,
	false
> {
	permission?: UserPermission;
}

const UserGroupAutoListInner = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	{
		permission,
		filterOptions,
		getOptionDisabled = defaultGetOptionDisabled,
		getOptionLabel = defaultGetOptionLabel,
		getOptionSelected = defaultGetOptionSelected,
		onClose,
		onInputChange,
		onOpen,
		...props
	}: UserGroupAutoListProps<Multiple, DisableClearable>,
	ref: UserGroupAutoListRef,
) => {
	const {loadingOptions, query, queryStr, setQueryStr, setIsOpen} = useAutoList(
		{
			filterOptions,
			isQueryValid,
			queryFn: getUserGroupAutoList,
			params: {permission},
		},
	);

	const placeholder =
		props.multiple !== true ? UserGroupAutoListLabel.PLACEHOLDER : undefined;

	return (
		<AutoListField<IUserGroupAutoListItem, Multiple, DisableClearable, false>
			ref={ref}
			hasSecondaryText
			label={UserGroupAutoListLabel.INPUT}
			loadingOptions={loadingOptions}
			multiple={false as Multiple}
			name="user-group-auto-list"
			placeholder={placeholder}
			filterOptions={filterOptions}
			// this typing is not taking into account multiple and is just using (T, T)
			// @ts-ignore
			isOptionEqualToValue={getOptionSelected}
			renderOption={(props, option, state) => (
				<AutoListFieldOption
					avatar
					icon={option.id === -1 ? faUsers : undefined}
					secondary={option.email}
					value={getOptionLabel(option)}
					{...props}
				/>
			)}
			testValueContainsInput={testValueContains}
			testValueContainsOption={testValueContains}
			inputValue={queryStr}
			{...props}
			options={query.data ?? []}
			getOptionDisabled={getOptionDisabled}
			getOptionLabel={getOptionLabel}
			onClose={splitEventHandler(onClose, () => setIsOpen(false))}
			onOpen={splitEventHandler(onOpen, () => setIsOpen(true))}
			onInputChange={splitEventHandler(onInputChange, (event, value, reason) =>
				setQueryStr(value),
			)}
		/>
	);
};

export const UserGroupAutoListPrimitive = React.forwardRef(
	UserGroupAutoListInner,
) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: UserGroupAutoListProps<Multiple, DisableClearable>,
) => JSX.Element;

/**
 * Autolist groups/users in one component
 */
export const UserGroupAutoList = UserGroupAutoListPrimitive;

export default UserGroupAutoList;
